import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import { API_BASE_URL } from '../../config/api';
import { authHeaders } from '../../services/auth';
import DataTable from '../../components/ui/DataTable';
import Spinner from '../../components/ui/Spinner';
import ToastProvider, { useToast } from '../../components/ui/ToastProvider';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import Modal from '../../components/ui/Modal';
import { adminTheme } from '../../theme-admin';
import { cache } from '../../utils/cache';

// Page Layout
const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${adminTheme.spacing.xl};
  animation: slideIn 0.3s ease-out;
`;

const Title = styled.h2`
  font-family: ${adminTheme.fonts.heading};
  font-size: ${adminTheme.fontSizes['3xl']};
  font-weight: ${adminTheme.fontWeights.bold};
  color: ${adminTheme.colors.gray[900]};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${adminTheme.spacing.md};
`;

const Stats = styled.div`
  display: flex;
  gap: ${adminTheme.spacing.md};
  font-size: ${adminTheme.fontSizes.sm};
`;

const StatBadge = styled.div`
  padding: ${adminTheme.spacing.sm} ${adminTheme.spacing.md};
  background: ${props => props.color || adminTheme.colors.gray[100]};
  color: ${props => props.textColor || adminTheme.colors.gray[700]};
  border-radius: ${adminTheme.borderRadius.lg};
  font-weight: ${adminTheme.fontWeights.semibold};
`;

const TableWrapper = styled.div`
  background: ${adminTheme.colors.background.primary};
  border: 1px solid ${adminTheme.colors.gray[200]};
  border-radius: ${adminTheme.borderRadius['2xl']};
  padding: ${adminTheme.spacing.xl};
  box-shadow: ${adminTheme.shadows.md};
`;

const ActionButton = styled.button`
  padding: ${adminTheme.spacing.sm} ${adminTheme.spacing.md};
  border-radius: ${adminTheme.borderRadius.lg};
  font-weight: ${adminTheme.fontWeights.semibold};
  font-size: ${adminTheme.fontSizes.sm};
  cursor: pointer;
  transition: ${adminTheme.transitions.fast};
  border: 1px solid ${adminTheme.colors.gray[300]};
  background: ${adminTheme.colors.background.primary};
  color: ${adminTheme.colors.gray[700]};

  &:hover {
    background: ${adminTheme.colors.gray[100]};
    transform: translateY(-1px);
  }
`;

const DeleteButton = styled(ActionButton)`
  border-color: ${adminTheme.colors.danger[300]};
  background: ${adminTheme.colors.danger[500]};
  color: white;

  &:hover {
    background: ${adminTheme.colors.danger[600]};
  }
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 4px 12px;
  border-radius: ${adminTheme.borderRadius.full};
  font-size: ${adminTheme.fontSizes.xs};
  font-weight: ${adminTheme.fontWeights.semibold};
  background: ${props => {
    if (props.status === 'resolved') return adminTheme.colors.success[100];
    if (props.status === 'pending') return adminTheme.colors.warning[100];
    return adminTheme.colors.gray[200];
  }};
  color: ${props => {
    if (props.status === 'resolved') return adminTheme.colors.success[700];
    if (props.status === 'pending') return adminTheme.colors.warning[700];
    return adminTheme.colors.gray[700];
  }};
`;

const ModalContent = styled.div`
  display: grid;
  gap: ${adminTheme.spacing.lg};
`;

const InfoRow = styled.div`
  display: grid;
  grid-template-columns: 120px 1fr;
  gap: ${adminTheme.spacing.md};
  padding: ${adminTheme.spacing.sm} 0;
  border-bottom: 1px solid ${adminTheme.colors.gray[200]};

  &:last-child {
    border-bottom: none;
  }
`;

const InfoLabel = styled.div`
  font-weight: ${adminTheme.fontWeights.semibold};
  color: ${adminTheme.colors.gray[700]};
  font-size: ${adminTheme.fontSizes.sm};
`;

const InfoValue = styled.div`
  color: ${adminTheme.colors.gray[900]};
  font-size: ${adminTheme.fontSizes.base};
  word-break: break-word;
`;

const MessageBox = styled.div`
  background: ${adminTheme.colors.gray[50]};
  padding: ${adminTheme.spacing.md};
  border-radius: ${adminTheme.borderRadius.lg};
  border-left: 4px solid ${adminTheme.colors.primary[500]};
  margin-top: ${adminTheme.spacing.md};
`;

const MessageText = styled.p`
  color: ${adminTheme.colors.gray[800]};
  line-height: 1.6;
  margin: 0;
  white-space: pre-wrap;
`;

const ActionBar = styled.div`
  display: flex;
  gap: ${adminTheme.spacing.sm};
  justify-content: flex-end;
  padding-top: ${adminTheme.spacing.md};
  border-top: 1px solid ${adminTheme.colors.gray[200]};
`;

const MarkButton = styled.button`
  padding: ${adminTheme.spacing.sm} ${adminTheme.spacing.lg};
  border-radius: ${adminTheme.borderRadius.lg};
  font-weight: ${adminTheme.fontWeights.semibold};
  font-size: ${adminTheme.fontSizes.sm};
  cursor: pointer;
  transition: ${adminTheme.transitions.fast};
  border: 1px solid ${props => props.variant === 'success' ? adminTheme.colors.success[500] : adminTheme.colors.gray[300]};
  background: ${props => props.variant === 'success' ? adminTheme.colors.success[500] : adminTheme.colors.background.primary};
  color: ${props => props.variant === 'success' ? 'white' : adminTheme.colors.gray[700]};

  &:hover {
    transform: translateY(-1px);
    box-shadow: ${adminTheme.shadows.sm};
  }
`;

function PageBody() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [confirmId, setConfirmId] = useState(null);
  const [viewing, setViewing] = useState(null);
  const toast = useToast();

  const load = useCallback(async (forceRefresh = false) => {
    if (!forceRefresh) {
      const cached = cache.get('contact-list');
      if (cached) {
        setItems(cached);
        return;
      }
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/contact`, { headers: authHeaders() });
      const data = await res.json();
      let submissions = [];
      // Handle different response formats
      if (data.data?.submissions && Array.isArray(data.data.submissions)) {
        submissions = data.data.submissions;
      } else if (Array.isArray(data.data)) {
        submissions = data.data;
      } else if (Array.isArray(data)) {
        submissions = data;
      }
      setItems(submissions);
      cache.set('contact-list', submissions);
    } catch (error) {
      toast.error('Failed to load contact submissions');
      setItems([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openView = (submission) => {
    setViewing(submission);
  };

  const closeView = () => {
    setViewing(null);
  };

  const markAsResolved = async (id) => {
    try {
      const res = await fetch(`${API_BASE_URL}/contact/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders()
        },
        body: JSON.stringify({ status: 'resolved' })
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Failed to update status');
      }

      toast.success('Marked as resolved! ✅');
      cache.invalidate('contact-list');
      load(true);
      if (viewing && viewing._id === id) {
        closeView();
      }
    } catch (error) {
      toast.error(error.message || 'Failed to update status');
    }
  };

  const onDelete = async (id) => {
    setConfirmId(id);
  };

  const confirmDelete = async () => {
    const id = confirmId;
    setConfirmId(null);
    try {
      const res = await fetch(`${API_BASE_URL}/contact/${id}`, {
        method: 'DELETE',
        headers: authHeaders()
      });
      if (!res.ok) throw new Error('delete failed');
      toast.success('Submission deleted');
      cache.invalidate('contact-list');
      load(true);
      if (viewing && viewing._id === id) {
        closeView();
      }
    } catch (error) {
      toast.error('Delete failed');
    }
  };

  const pendingCount = items.filter(i => i.status !== 'resolved').length;
  const resolvedCount = items.filter(i => i.status === 'resolved').length;

  return (
    <div>
      <PageHeader>
        <Title>
          <span>📧</span>
          Contact Form Submissions
        </Title>
        <Stats>
          <StatBadge color={adminTheme.colors.warning[100]} textColor={adminTheme.colors.warning[700]}>
            ⏳ Pending: {pendingCount}
          </StatBadge>
          <StatBadge color={adminTheme.colors.success[100]} textColor={adminTheme.colors.success[700]}>
            ✅ Resolved: {resolvedCount}
          </StatBadge>
        </Stats>
      </PageHeader>

      <TableWrapper>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
            <Spinner label="Loading submissions..." />
          </div>
        ) : items.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: adminTheme.colors.gray[500] }}>
            <p style={{ fontSize: adminTheme.fontSizes.lg, margin: 0 }}>📭 No contact submissions yet</p>
          </div>
        ) : (
          <DataTable
            columns={[
              { key: 'name', header: 'Name', width: '1fr' },
              { key: 'email', header: 'Email', width: '1.2fr' },
              { key: 'phone', header: 'Phone', width: '1fr' },
              { key: 'course', header: 'Course Interest', width: '1fr' },
              { 
                key: 'status', 
                header: 'Status', 
                width: '0.8fr',
                render: (s) => (
                  <StatusBadge status={s.status || 'pending'}>
                    {s.status === 'resolved' ? '✅ Resolved' : '⏳ Pending'}
                  </StatusBadge>
                )
              },
              {
                key: 'createdAt',
                header: 'Submitted',
                width: '0.8fr',
                render: (s) => s.createdAt ? new Date(s.createdAt).toLocaleDateString() : '-'
              },
            ]}
            rows={items}
            actions={(s) => (
              <>
                <ActionButton onClick={() => openView(s)}>👁️ View</ActionButton>
                <DeleteButton onClick={() => onDelete(s._id)}>🗑️ Delete</DeleteButton>
              </>
            )}
          />
        )}
      </TableWrapper>

      <ConfirmDialog
        open={!!confirmId}
        onCancel={() => setConfirmId(null)}
        onConfirm={confirmDelete}
        title="Delete submission?"
        description="This action cannot be undone."
      />

      <Modal
        open={!!viewing}
        title="📧 Contact Submission Details"
        onCancel={closeView}
        hideFooter
      >
        {viewing && (
          <ModalContent>
            <InfoRow>
              <InfoLabel>Name:</InfoLabel>
              <InfoValue>{viewing.name || '-'}</InfoValue>
            </InfoRow>

            <InfoRow>
              <InfoLabel>Email:</InfoLabel>
              <InfoValue>
                <a href={`mailto:${viewing.email}`} style={{ color: adminTheme.colors.primary[600] }}>
                  {viewing.email}
                </a>
              </InfoValue>
            </InfoRow>

            <InfoRow>
              <InfoLabel>Phone:</InfoLabel>
              <InfoValue>
                <a href={`tel:${viewing.phone}`} style={{ color: adminTheme.colors.primary[600] }}>
                  {viewing.phone}
                </a>
              </InfoValue>
            </InfoRow>

            <InfoRow>
              <InfoLabel>Course Interest:</InfoLabel>
              <InfoValue>{viewing.course || 'Not specified'}</InfoValue>
            </InfoRow>

            <InfoRow>
              <InfoLabel>Status:</InfoLabel>
              <InfoValue>
                <StatusBadge status={viewing.status || 'pending'}>
                  {viewing.status === 'resolved' ? '✅ Resolved' : '⏳ Pending'}
                </StatusBadge>
              </InfoValue>
            </InfoRow>

            <InfoRow>
              <InfoLabel>Submitted:</InfoLabel>
              <InfoValue>
                {viewing.createdAt ? new Date(viewing.createdAt).toLocaleString() : '-'}
              </InfoValue>
            </InfoRow>

            <MessageBox>
              <InfoLabel style={{ marginBottom: adminTheme.spacing.sm }}>Message:</InfoLabel>
              <MessageText>{viewing.message || 'No message provided'}</MessageText>
            </MessageBox>

            <ActionBar>
              {viewing.status !== 'resolved' && (
                <MarkButton
                  variant="success"
                  onClick={() => markAsResolved(viewing._id)}
                >
                  ✅ Mark as Resolved
                </MarkButton>
              )}
              <DeleteButton onClick={() => onDelete(viewing._id)}>
                🗑️ Delete
              </DeleteButton>
            </ActionBar>
          </ModalContent>
        )}
      </Modal>
    </div>
  );
}

export default function AdminContactForms() {
  return (
    <ToastProvider>
      <PageBody />
    </ToastProvider>
  );
}
